import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/api/client";

/** In-app feedback (`app/api/feedback.py`).
 *
 *  Any signed-in user sends a bug, idea or other note from the app bar,
 *  with the context the dialog shows them and an optional screenshot.
 *  Platform admins read every submission on the admin Feedback page; an
 *  organisation's admin reads their own organisation's. Refusals are
 *  stable `feedback_*` codes the SPA translates. */

export type FeedbackCategory = "bug" | "idea" | "other";
/** Triage (new → seen → done), plus a request's outcome, which only
 *  Operations → Requests sets. */
export type FeedbackStatus = "new" | "seen" | "done" | "answered" | "linked" | "declined";

export const FEEDBACK_CATEGORIES: readonly FeedbackCategory[] = ["bug", "idea", "other"];
/** The statuses the Feedback page sets (and filters by). */
export const FEEDBACK_STATUSES: readonly FeedbackStatus[] = ["new", "seen", "done"];
/** A request's outcome: shown on the Feedback page, never set there. */
export const REQUEST_OUTCOMES: readonly FeedbackStatus[] = ["answered", "linked", "declined"];

export type FeedbackConfig = {
  enabled: boolean;
  /** The organisation a submission is filed under (null: none), as the
   *  server resolved it. */
  org_slug: string | null;
  org_name: string | null;
  max_message_chars: number;
  max_screenshot_bytes: number;
};

export type FeedbackContext = {
  route?: string | null;
  session_id?: string | null;
  app_version?: string | null;
  server_version?: string | null;
  user_agent?: string | null;
  ui_language?: string | null;
};

export type FeedbackItem = {
  id: string;
  created_at: string;
  updated_at: string;
  category: FeedbackCategory;
  message: string;
  status: FeedbackStatus;
  context: FeedbackContext;
  has_screenshot: boolean;
  screenshot_content_type: string | null;
  user: { id: string; email: string; display_name: string } | null;
  org: { id: string; slug: string; name: string } | null;
  /** null: no address configured; pending until the send reports back. */
  email_status: "pending" | "sent" | "failed" | null;
  /** A code when the send failed (see ``FEEDBACK_EMAIL_ERRORS``). */
  email_error: string | null;
};

/** The codes ``email_error`` holds (``app.services.feedback``). */
export const FEEDBACK_EMAIL_ERRORS = [
  "email_provider_not_configured",
  "email_not_delivered",
  "email_send_error",
] as const;

export type FeedbackList = {
  items: FeedbackItem[];
  total: number;
  /** Per status; a request outcome appears once the server counts it. */
  counts: Partial<Record<FeedbackStatus, number>>;
  org_slug: string | null;
  all_orgs: boolean;
};

export type FeedbackSubmission = {
  category: FeedbackCategory;
  message: string;
  orgSlug: string | null;
  route: string;
  sessionId: string | null;
  appVersion: string | null;
  uiLanguage: string;
  screenshot: File | null;
  /** ``ask_panel``: a request from the Ask panel (the server refuses it
   *  while requests are off). Omitted: plain feedback. */
  source?: "ask_panel";
};

const configKey = (orgSlug: string | null) => ["feedback", "config", orgSlug] as const;

/** Whether to offer the dialog. ``orgSlug`` is the org the user pinned;
 *  null lets the server resolve it (their own organisation). */
export function useFeedbackConfig(orgSlug: string | null, enabled = true) {
  return useQuery({
    queryKey: configKey(orgSlug),
    queryFn: async () => {
      const query = orgSlug ? `?org=${encodeURIComponent(orgSlug)}` : "";
      return (await api.get<FeedbackConfig>(`/feedback/config${query}`)).data;
    },
    enabled,
    staleTime: 5 * 60_000,
    retry: false,
  });
}

export function useSubmitFeedback() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: FeedbackSubmission) => {
      const fd = new FormData();
      fd.append("category", payload.category);
      fd.append("message", payload.message);
      if (payload.orgSlug) fd.append("org_slug", payload.orgSlug);
      fd.append("route", payload.route);
      if (payload.sessionId) fd.append("session_id", payload.sessionId);
      if (payload.appVersion) fd.append("app_version", payload.appVersion);
      fd.append("ui_language", payload.uiLanguage);
      if (payload.screenshot) fd.append("screenshot", payload.screenshot);
      if (payload.source) fd.append("source", payload.source);
      const r = await api.post<{ id: string; created_at: string }>("/feedback", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return r.data;
    },
    // A request sent from the Ask panel shows up in My requests.
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: ["admin", "feedback"] }),
        qc.invalidateQueries({ queryKey: ["requests"] }),
      ]),
  });
}

/** The admin list. The server scopes it: a platform admin sees the org
 *  picked in the admin area (or all), an org admin their own org. */
export function useAdminFeedback(params: {
  status: FeedbackStatus | "all";
  activeOrgSlug: string | null;
  limit?: number;
}) {
  const search = new URLSearchParams({ status: params.status, limit: String(params.limit ?? 100) });
  return useQuery({
    queryKey: ["admin", "feedback", params.status, params.activeOrgSlug, params.limit ?? 100],
    queryFn: async () =>
      (await api.get<FeedbackList>(`/admin/feedback?${search.toString()}`)).data,
    refetchInterval: 60_000,
    retry: false,
  });
}

export function useSetFeedbackStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { id: string; status: FeedbackStatus }) =>
      (
        await api.patch<FeedbackItem>(`/admin/feedback/${payload.id}`, {
          status: payload.status,
        })
      ).data,
    onSettled: () => qc.invalidateQueries({ queryKey: ["admin", "feedback"] }),
  });
}

/** Browser URL of a submission's screenshot (served by the API). */
export function feedbackScreenshotSrc(id: string): string {
  return `/api/admin/feedback/${encodeURIComponent(id)}/screenshot`;
}

/** The i18n key for a `feedback_*` refusal; null for anything else. */
export function feedbackErrorKey(err: unknown): string | null {
  const detail = (err as { response?: { data?: { detail?: unknown } } } | null)?.response?.data
    ?.detail;
  if (typeof detail !== "string" || !detail.startsWith("feedback_")) return null;
  const known = new Set([
    "feedback_disabled",
    "feedback_body_too_large",
    "feedback_forbidden",
    "feedback_org_required",
    "feedback_not_found",
    "feedback_org_not_found",
    "feedback_category_invalid",
    "feedback_status_invalid",
    "feedback_message_empty",
    "feedback_message_too_long",
    "feedback_rate_limited",
    "feedback_screenshot_too_large",
    "feedback_screenshot_too_many_pixels",
    "feedback_screenshot_type_unsupported",
    "feedback_screenshot_unreadable",
  ]);
  return known.has(detail) ? `feedback.errors.${detail.slice("feedback_".length)}` : null;
}
